import httpClient from './httpClient';
import { parseApiError, retryOperation, validators } from '../utils/errorHandler';

const getPurchaseOrders = async (page = 1, pageSize = 10, filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    // Remove undefined values to clean up the request
    ...(filters.purchase_order_id && { purchase_order_id: filters.purchase_order_id }),
    ...(filters.supplier_id && { supplier_id: filters.supplier_id }),
    ...(filters.supplier_name && { supplier_name: filters.supplier_name }),
    ...(filters.status && { status: filters.status }),
    ...(filters.approval_status && { approval_status: filters.approval_status }),
    ...(filters.fulfillment_status && { fulfillment_status: filters.fulfillment_status }),
    ...(filters.start_date && { start_date: filters.start_date }),
    ...(filters.end_date && { end_date: filters.end_date }),
    ...(filters.sort_by && { sort_by: filters.sort_by }),
    ...(filters.sort_order && { sort_order: filters.sort_order }),
  };

  try {
    const response = await httpClient.get('/api/purchase-orders', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase orders:', error);
    throw error;
  }
};

const getPurchaseOrder = async (orderId, cacheBust = null) => {
  try {
    const params = cacheBust ? { _t: cacheBust } : {};
    const response = await httpClient.get(`/api/purchase-orders/${encodeURIComponent(orderId)}`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase order:', error);
    throw error;
  }
};

const createPurchaseOrder = async (orderData) => {
  // Validate data before sending to API
  const validationErrors = validators.purchaseOrder(orderData);
  if (validationErrors.length > 0) {
    throw validationErrors[0]; // Throw first validation error
  }

  try {
    const response = await retryOperation(async () => {
      return await httpClient.post('/api/purchases/new', orderData);
    }, 2); // Retry up to 2 times for transient errors
    
    return response.data;
  } catch (error) {
    const parsedError = parseApiError(error);
    console.error('Error creating purchase order:', parsedError);
    throw parsedError;
  }
};

const updatePurchaseOrder = async (orderId, orderData) => {
  // Validate data before sending to API
  const validationErrors = validators.purchaseOrder(orderData);
  if (validationErrors.length > 0) {
    throw validationErrors[0];
  }

  try {
    const response = await retryOperation(async () => {
      return await httpClient.put(`/api/purchase-orders/${encodeURIComponent(orderId)}`, orderData);
    }, 2);
    
    return response.data;
  } catch (error) {
    const parsedError = parseApiError(error);
    console.error('Error updating purchase order:', parsedError);
    throw parsedError;
  }
};

const approvePurchaseOrder = async (orderId) => {
  try {
    const response = await httpClient.post(`/api/purchase-orders/${encodeURIComponent(orderId)}/approve`, {});
    return response.data;
  } catch (error) {
    console.error('Error approving purchase order:', error);
    throw error;
  }
};

const rejectPurchaseOrder = async (orderId, reason = '') => {
  try {
    const response = await httpClient.post(`/api/purchase-orders/${encodeURIComponent(orderId)}/reject`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting purchase order:', error);
    throw error;
  }
};

const submitPurchaseOrder = async (orderId) => {
  try {
    const response = await httpClient.post(`/api/purchase-orders/${encodeURIComponent(orderId)}/submit`);
    return response.data;
  } catch (error) {
    console.error('Error submitting purchase order:', error);
    throw error;
  }
};

const addReceipt = async (orderId, receiptData, purchaseOrder = null) => {
  // If receiptData has received_items (transformed), convert back to receipt_items for validation
  let validationData = receiptData;
  if (receiptData.received_items && !receiptData.receipt_items) {
    validationData = {
      ...receiptData,
      receipt_items: receiptData.received_items.map(item => ({
        ...item,
        received_quantity: item.quantity_received,
        product_code: item.product_code
      }))
    };
  }
  
  // Validate receipt data using the validation format
  const validationErrors = validators.receipt(validationData, purchaseOrder);
  if (validationErrors.length > 0) {
    throw validationErrors[0];
  }

  try {
    console.log('=== PURCHASE SERVICE DEBUG ===');
    console.log('Sending receipt data to backend:', JSON.stringify(receiptData, null, 2));
    console.log('Inspector name in service:', receiptData.inspector_name);
    console.log('=== END SERVICE DEBUG ===');
    
    const response = await retryOperation(async () => {
      return await httpClient.post(`/api/purchase-orders/${encodeURIComponent(orderId)}/receipts`, receiptData);
    }, 2);
    
    console.log('=== RECEIPT CREATION RESPONSE ===');
    console.log('Response data:', response.data);
    console.log('=== END RESPONSE ===');
    
    return response.data;
  } catch (error) {
    const parsedError = parseApiError(error);
    console.error('Error adding receipt:', parsedError);
    throw parsedError;
  }
};

const getReceipts = async (orderId) => {
  try {
    const response = await httpClient.get(`/api/purchase-orders/${encodeURIComponent(orderId)}/receipts`);
    return response.data;
  } catch (error) {
    console.error('Error fetching receipts:', error);
    throw error;
  }
};

const updateReceipt = async (receiptId, receiptData) => {
  try {
    const response = await httpClient.put(`/api/receipts/${encodeURIComponent(receiptId)}`, receiptData);
    return response.data;
  } catch (error) {
    console.error('Error updating receipt:', error);
    throw error;
  }
};

const deleteReceipt = async (orderId, receiptId) => {
  try {
    const response = await httpClient.delete(`/api/purchase-orders/${encodeURIComponent(orderId)}/receipts/${encodeURIComponent(receiptId)}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting receipt:', error);
    throw error;
  }
};

const cancelPurchaseOrder = async (orderId) => {
  try {
    const response = await httpClient.delete(`/api/purchase-orders/${encodeURIComponent(orderId)}`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling purchase order:', error);
    throw error;
  }
};

const getPurchaseOrderStats = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  try {
    const response = await httpClient.get('/api/purchase-orders/stats', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase order stats:', error);
    throw error;
  }
};

// Sales to Purchase Conversion
const convertSalesOrderToPurchase = async (salesOrderId, conversionData = {}) => {
  try {
    const response = await httpClient.post(`/api/sales-orders/${encodeURIComponent(salesOrderId)}/convert-to-purchase`, conversionData);
    return response.data;
  } catch (error) {
    console.error('Error converting sales order to purchase:', error);
    throw error;
  }
};

const getPurchaseOrderLinkStatus = async () => {
  try {
    const response = await httpClient.get('/api/purchase-orders/link-status');
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase order link status:', error);
    throw error;
  }
};

// SO-PO Mapping Functions
const getSourceSalesOrder = async (purchaseOrderId) => {
  try {
    const response = await httpClient.get(`/api/purchase-orders/${encodeURIComponent(purchaseOrderId)}/source-sales-order`);
    return response.data;
  } catch (error) {
    console.error('Error fetching source sales order:', error);
    throw error;
  }
};

const bulkPurchaseOperations = async (operations) => {
  try {
    const response = await httpClient.post('/api/purchase-orders/bulk-operations', { operations });
    return response.data;
  } catch (error) {
    console.error('Error performing bulk purchase operations:', error);
    throw error;
  }
};

// Cost Analytics
const getCostAnalytics = async (days = 30) => {
  const params = { days };

  try {
    const response = await httpClient.get('/api/analytics/cost-analysis', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching cost analytics:', error);
    throw error;
  }
};

const getVendorPerformance = async (startDate, endDate, limit = 50) => {
  const params = { limit };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  try {
    const response = await httpClient.get('/api/analytics/vendor-performance', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching vendor performance:', error);
    throw error;
  }
};

// Note: getProfitAnalysis endpoint temporarily removed in refactored backend
// Can be re-added when comprehensive profit analysis is implemented

const getAdminDashboard = async (startDate, endDate) => {
  const params = {};

  // Add date parameters if provided
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  try {
    const response = await httpClient.get('/api/analytics/dashboard', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching admin dashboard:', error);
    throw error;
  }
};

// Suppliers
const getSuppliers = async (page = 1, pageSize = 50, filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    ...(filters.supplier_name && { supplier_name: filters.supplier_name }),
  };

  try {
    const response = await httpClient.get('/api/suppliers', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

const getSupplier = async (supplierId) => {
  try {
    const response = await httpClient.get(`/api/suppliers/${supplierId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier:', error);
    throw error;
  }
};

const createSupplier = async (supplierData) => {
  // Validate supplier data
  const validationErrors = validators.supplier(supplierData);
  if (validationErrors.length > 0) {
    throw validationErrors[0];
  }

  try {
    const response = await retryOperation(async () => {
      return await httpClient.post('/api/suppliers', supplierData);
    }, 2);
    
    return response.data;
  } catch (error) {
    const parsedError = parseApiError(error);
    console.error('Error creating supplier:', parsedError);
    throw parsedError;
  }
};

const updateSupplier = async (supplierId, supplierData) => {
  try {
    const response = await httpClient.put(`/api/suppliers/${supplierId}`, supplierData);
    return response.data;
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw error;
  }
};

const deleteSupplier = async (supplierId) => {
  try {
    const response = await httpClient.delete(`/api/suppliers/${supplierId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting supplier:', error);
    throw error;
  }
};

// Unlink a purchase order from its source sales order and optionally delete the PO
const unlinkPurchaseOrderFromSales = async (purchaseOrderId, deletePo = true) => {
  try {
    const response = await httpClient.post(`/api/purchase-orders/${encodeURIComponent(purchaseOrderId)}/unlink-from-sales`, {
      delete_po: deletePo
    });
    return response.data;
  } catch (error) {
    console.error('Error unlinking purchase order from sales:', error);
    throw error;
  }
};

// Helper function to parse ObjectId data similar to other services
const parseObjectIdData = (data) => {
  if (!data) return data;
  
  if (Array.isArray(data)) {
    return data.map(parseObjectIdData);
  }
  
  if (typeof data === 'object') {
    const parsed = {};
    for (const [key, value] of Object.entries(data)) {
      if (key === '_id' && typeof value === 'object' && value.$oid) {
        parsed[key] = value.$oid;
      } else if (key.endsWith('_id') && typeof value === 'object' && value.$oid) {
        parsed[key] = value.$oid;
      } else if (key.endsWith('_at') && typeof value === 'object' && value.$date) {
        parsed[key] = new Date(value.$date);
      } else if (typeof value === 'object' && value !== null) {
        parsed[key] = parseObjectIdData(value);
      } else {
        parsed[key] = value;
      }
    }
    return parsed;
  }
  
  return data;
};

// Wrapper functions that automatically parse ObjectId data
const wrappedGetPurchaseOrders = async (...args) => {
  const data = await getPurchaseOrders(...args);
  return parseObjectIdData(data);
};

const wrappedGetPurchaseOrder = async (...args) => {
  const data = await getPurchaseOrder(...args);
  return parseObjectIdData(data);
};

const wrappedGetSuppliers = async (...args) => {
  const data = await getSuppliers(...args);
  return parseObjectIdData(data);
};

const wrappedGetSupplier = async (...args) => {
  const data = await getSupplier(...args);
  return parseObjectIdData(data);
};

// Export purchase order to Excel
const exportPurchaseOrderToExcel = async (orderId) => {
  try {
    const response = await httpClient.get(`/api/purchase-orders/${encodeURIComponent(orderId)}/export`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting purchase order:', error);
    throw error;
  }
};

/**
 * Preview product sync changes (NO database modifications)
 * @param {string} orderId - Purchase order ID
 * @param {Object} options - Sync options
 * @param {string[]} options.product_ids - Specific product IDs to preview sync (optional, defaults to all)
 * @param {string[]} options.sync_fields - Fields to sync (optional, defaults to all non-pricing fields)
 * @returns {Promise<Object>} Preview result with changes, affected_count, total_products
 */
const previewSyncPurchaseOrderProducts = async (orderId, options = {}) => {
  try {
    const response = await httpClient.post(`/api/purchases/${orderId}/preview-sync-products`, options);
    return response.data;
  } catch (error) {
    console.error('Error previewing purchase order product sync:', error);
    throw error;
  }
};

// Sync product information from products collection to purchase order
/**
 * Sync product information from products collection to purchase order
 * @param {string} orderId - Purchase order ID
 * @param {Object} options - Sync options
 * @param {string[]} options.product_ids - Specific product IDs to sync (optional, defaults to all)
 * @param {string[]} options.sync_fields - Fields to sync (optional, defaults to all non-pricing fields)
 * @returns {Promise<Object>} Sync result with changes
 */
const syncPurchaseOrderProducts = async (orderId, options = {}) => {
  try {
    const response = await httpClient.post(`/api/purchases/${orderId}/sync-products`, options);
    return response.data;
  } catch (error) {
    console.error('Error syncing purchase order products:', error);
    throw error;
  }
};

/**
 * Get unassigned purchase orders (not linked to any sales order)
 * @param {number} page - Page number
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter options
 * @param {string} filters.client_id - Filter by client ID
 * @param {string} filters.search - Search in PO ID or supplier name
 * @returns {Promise<Object>} Unassigned purchase orders with pagination
 */
const getUnassignedPurchaseOrders = async (page = 1, pageSize = 10, filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    ...(filters.client_id && { client_id: filters.client_id }),
    ...(filters.search && { search: filters.search }),
  };

  try {
    const response = await httpClient.get('/api/purchase-orders/unassigned', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching unassigned purchase orders:', error);
    throw error;
  }
};

// Enhanced error handling wrapper for API calls
const withErrorHandling = (fn) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      const parsedError = parseApiError(error);
      console.error(`API Error in ${fn.name}:`, parsedError);
      throw parsedError;
    }
  };
};

// Validation utilities for purchase operations
const validateData = {
  purchaseOrder: (data) => {
    const errors = validators.purchaseOrder(data);
    if (errors.length > 0) {
      throw errors[0];
    }
    return true;
  },
  
  receipt: (data, purchaseOrder) => {
    const errors = validators.receipt(data, purchaseOrder);
    if (errors.length > 0) {
      throw errors[0];
    }
    return true;
  },
  
  supplier: (data) => {
    const errors = validators.supplier(data);
    if (errors.length > 0) {
      throw errors[0];
    }
    return true;
  },
  
  dateRange: (startDate, endDate) => {
    const errors = validators.dateRange(startDate, endDate);
    if (errors.length > 0) {
      throw errors[0];
    }
    return true;
  }
};

const purchaseService = {
  // Core CRUD operations
  getPurchaseOrders: wrappedGetPurchaseOrders,
  getPurchaseOrder: wrappedGetPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder,
  cancelPurchaseOrder: withErrorHandling(cancelPurchaseOrder),

  // Approval workflow
  approvePurchaseOrder: withErrorHandling(approvePurchaseOrder),
  rejectPurchaseOrder: withErrorHandling(rejectPurchaseOrder),
  submitPurchaseOrder: withErrorHandling(submitPurchaseOrder),

  // Receipt management
  addReceipt,
  getReceipts: withErrorHandling(getReceipts),
  updateReceipt: withErrorHandling(updateReceipt),
  deleteReceipt: withErrorHandling(deleteReceipt),

  // Statistics and reporting
  getPurchaseOrderStats: withErrorHandling(getPurchaseOrderStats),

  // Sales order conversion
  convertSalesOrderToPurchase: withErrorHandling(convertSalesOrderToPurchase),
  getPurchaseOrderLinkStatus: withErrorHandling(getPurchaseOrderLinkStatus),

  // SO-PO Mapping
  getSourceSalesOrder: withErrorHandling(getSourceSalesOrder),
  bulkPurchaseOperations: withErrorHandling(bulkPurchaseOperations),

  // Analytics
  getCostAnalytics: withErrorHandling(getCostAnalytics),
  getVendorPerformance: withErrorHandling(getVendorPerformance),
  getAdminDashboard: withErrorHandling(getAdminDashboard),

  // Suppliers
  getSuppliers: withErrorHandling(wrappedGetSuppliers),
  getSupplier: withErrorHandling(wrappedGetSupplier),
  createSupplier,
  updateSupplier: withErrorHandling(updateSupplier),
  deleteSupplier: withErrorHandling(deleteSupplier),
  unlinkPurchaseOrderFromSales: withErrorHandling(unlinkPurchaseOrderFromSales),

  // Export functionality
  exportPurchaseOrderToExcel: withErrorHandling(exportPurchaseOrderToExcel),

  // Product sync
  previewSyncPurchaseOrderProducts: withErrorHandling(previewSyncPurchaseOrderProducts),
  syncPurchaseOrderProducts: withErrorHandling(syncPurchaseOrderProducts),

  // SO-PO Linking
  getUnassignedPurchaseOrders: withErrorHandling(getUnassignedPurchaseOrders),

  // Utilities
  parseObjectIdData,
  validateData,
  withErrorHandling
};

export default purchaseService;
